/**
 * Native bridge helpers for the Capacitor iOS shell. Everything is
 * platform-gated + dynamic-imported, so it's a pure no-op on the web and adds
 * nothing to the SSR bundle. The SSR layer detects the app from the User-Agent
 * ('TBNCApp'); this file is the client-side runtime side.
 */

let checked = false;
let native = false;

/** True only inside the Capacitor native webview. */
export function isNativeApp(): boolean {
  if (checked) return native;
  checked = true;
  try {
    const cap = (globalThis as { Capacitor?: { isNativePlatform?: () => boolean } }).Capacitor;
    native = Boolean(cap?.isNativePlatform?.());
  } catch {
    native = false;
  }
  return native;
}

/** Light haptic tap on interaction. No-op off-native. */
export async function haptic(style: 'light' | 'medium' = 'light'): Promise<void> {
  if (!isNativeApp()) return;
  try {
    const { Haptics, ImpactStyle } = await import('@capacitor/haptics');
    await Haptics.impact({ style: style === 'medium' ? ImpactStyle.Medium : ImpactStyle.Light });
  } catch {
    /* haptics unavailable — ignore */
  }
}

/** One-time native chrome init: theme the status bar + hide the splash. */
export async function initNativeShell(): Promise<void> {
  if (!isNativeApp()) return;
  try {
    const { StatusBar, Style } = await import('@capacitor/status-bar');
    await StatusBar.setOverlaysWebView({ overlay: false });
    // Style.Light = dark text, for our light (paper) top area.
    await StatusBar.setStyle({ style: Style.Light });
  } catch {
    /* status bar unavailable — ignore */
  }
  try {
    const { SplashScreen } = await import('@capacitor/splash-screen');
    await SplashScreen.hide();
  } catch {
    /* splash unavailable — ignore */
  }
}

/**
 * Native share sheet. Returns false off-native (or on cancel/error) so callers
 * can fall back to copy-link / web share.
 */
export async function nativeShare(opts: {
  title?: string;
  text?: string;
  url: string;
}): Promise<boolean> {
  if (!isNativeApp()) return false;
  try {
    const { Share } = await import('@capacitor/share');
    await Share.share(opts);
    return true;
  } catch {
    return false;
  }
}
